/**
 * Theme Service
 * Handles all theme-related API calls to the FastAPI backend.
 */

import { apiRequest } from '@/lib/api-client'
import { logError, logInfo } from '@/lib/logger'

export interface Theme {
  id: string
  name: string
  display_name: string
  is_public: boolean
  client_id: string | null
  css_class: string
  background_image_url: string | null
  icon_color: string
  icon_text_color?: string
  accent_color?: string
  background_color?: string | null
  text_color?: string | null
  secondary_text_color?: string | null
  taskbar_color?: string | null
  titlebar_color?: string | null
  border_color?: string | null
  is_dark_mode: boolean
  created_at: string
  updated_at: string
}

/**
 * Theme create/update payload (without id, created_at, updated_at).
 */
export interface ThemePayload {
  name: string
  display_name: string
  is_public: boolean
  client_id: string | null
  css_class: string
  background_image_url: string | null
  icon_color: string
  icon_text_color?: string | null
  accent_color?: string | null
  background_color?: string | null
  text_color?: string | null
  secondary_text_color?: string | null
  taskbar_color?: string | null
  titlebar_color?: string | null
  border_color?: string | null
  is_dark_mode: boolean
}

/**
 * Partial theme update payload.
 */
export type ThemeUpdatePayload = Partial<ThemePayload>

/**
 * Response type for theme list endpoints.
 */
interface ThemeListResponse {
  themes: Theme[]
  total: number
}

/**
 * Response type for theme delete endpoint.
 */
interface ThemeDeleteResponse {
  message: string
  deleted_id: string
}

export const themeService = {
  /**
   * Get all themes available for a specific user.
   * Note: userId parameter is kept for API compatibility but not used -
   * the backend extracts user ID from JWT token.
   */
  async getThemesForUser(_userId?: string): Promise<Theme[]> {
    try {
      const response = await apiRequest<ThemeListResponse>('/themes')
      logInfo({ context: 'ThemeService' }, `Loaded ${response?.themes.length ?? 0} themes for user`)
      return response?.themes ?? []
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'getThemesForUser' })
      return []
    }
  },

  /**
   * Get a specific theme by ID.
   */
  async getThemeById(themeId: string): Promise<Theme | null> {
    try {
      return await apiRequest<Theme>(`/themes/${themeId}`)
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'getThemeById', themeId })
      return null
    }
  },

  /**
   * Get a theme by its name.
   */
  async getThemeByName(themeName: string): Promise<Theme | null> {
    try {
      return await apiRequest<Theme>(`/themes/by-name/${encodeURIComponent(themeName)}`)
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'getThemeByName', themeName })
      return null
    }
  },

  /**
   * Get the default theme for a specific client.
   * Falls back to the light theme if no default is set.
   */
  async getDefaultThemeForClient(clientId: string): Promise<Theme | null> {
    let theme: Theme | null
    try {
      theme = await apiRequest<Theme>(`/themes/client/${clientId}/default`)
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'getDefaultThemeForClient', clientId })
      theme = null
    }
    if (!theme) {
      return this.getThemeByName('light')
    }
    return theme
  },

  /**
   * Get all themes (admin only).
   */
  async getAllThemes(): Promise<Theme[]> {
    try {
      const response = await apiRequest<ThemeListResponse>('/themes/all')
      logInfo({ context: 'ThemeService' }, `Loaded ${response?.themes.length ?? 0} themes (admin)`)
      return response?.themes ?? []
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'getAllThemes' })
      return []
    }
  },

  /**
   * Check if a user has access to a specific theme.
   * This is done by attempting to fetch the theme - if 403, no access.
   * Note: userId parameter is kept for API compatibility but not used.
   */
  async canUserAccessTheme(_userId: string, themeId: string): Promise<boolean> {
    try {
      const theme = await this.getThemeById(themeId)
      return theme !== null
    } catch {
      return false
    }
  },

  /**
   * Create a new theme (admin only).
   */
  async createTheme(theme: ThemePayload): Promise<Theme | null> {
    try {
      const created = await apiRequest<Theme>('/themes', {
        method: 'POST',
        body: JSON.stringify(theme),
      })
      if (created) {
        logInfo({ context: 'ThemeService' }, `Created theme: ${created.display_name}`)
      }
      return created
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'createTheme' })
      throw error
    }
  },

  /**
   * Update an existing theme (admin only).
   */
  async updateTheme(themeId: string, updates: ThemeUpdatePayload): Promise<Theme | null> {
    try {
      const updated = await apiRequest<Theme>(`/themes/${themeId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      if (updated) {
        logInfo({ context: 'ThemeService' }, `Updated theme: ${updated.display_name}`)
      }
      return updated
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'updateTheme', themeId })
      throw error
    }
  },

  /**
   * Delete a theme (admin only).
   */
  async deleteTheme(themeId: string): Promise<boolean> {
    try {
      const response = await apiRequest<ThemeDeleteResponse>(`/themes/${themeId}`, {
        method: 'DELETE',
      })
      if (response) {
        logInfo({ context: 'ThemeService' }, `Deleted theme: ${response.deleted_id}`)
        return true
      }
      return false
    } catch (error) {
      logError(error as Error, { context: 'ThemeService', method: 'deleteTheme', themeId })
      throw error
    }
  },
}
